/**
 * LoadingSkeleton for ProgressTab — greyed-out blocks matching layout.
 * Spec §8.9 — same dimensions as loaded state to prevent layout shift.
 */

function Skel({ className }: { className: string }) {
  return <div className={`bg-slate-100 rounded animate-pulse ${className}`} />;
}

export function ProgressSkeleton({ mobile = false }: { mobile?: boolean }) {
  if (mobile) {
    return (
      <div className="space-y-4 pt-4">
        {/* Headline card */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 space-y-3">
          <Skel className="h-4 w-32" />
          <div className="flex gap-4">
            <Skel className="h-10 w-16" />
            <Skel className="h-10 w-16" />
          </div>
        </div>
        {/* Pillars */}
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-slate-200 space-y-2">
            <Skel className="h-4 w-12" />
            <Skel className="h-3 w-full" />
          </div>
        ))}
        {/* Attributions */}
        <div className="space-y-3">
          <Skel className="h-4 w-36" />
          {[1, 2].map((i) => (
            <Skel key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
        {/* Chart */}
        <Skel className="h-40 rounded-xl w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Headline band */}
      <div className="flex gap-8 items-center">
        <Skel className="h-8 w-40" />
        <Skel className="h-8 w-40" />
        <Skel className="h-8 w-48 ml-auto" />
      </div>
      {/* Split screen */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200 space-y-4">
          <Skel className="h-5 w-24" />
          <div className="flex gap-3">
            <Skel className="h-12 w-12 rounded-full" />
            <Skel className="h-12 w-12 rounded-full" />
            <Skel className="h-12 w-12 rounded-full" />
          </div>
          <Skel className="h-3 w-full" />
          <Skel className="h-3 w-2/3" />
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200 space-y-4">
          <Skel className="h-5 w-24" />
          <div className="flex gap-3">
            <Skel className="h-12 w-12 rounded-full" />
            <Skel className="h-12 w-12 rounded-full" />
            <Skel className="h-12 w-12 rounded-full" />
          </div>
          <Skel className="h-3 w-full" />
          <Skel className="h-3 w-2/3" />
        </div>
      </div>
      {/* Pillar drill-downs */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 space-y-2">
          <Skel className="h-5 w-12" />
          <Skel className="h-3 w-full" />
          <Skel className="h-3 w-3/4" />
        </div>
      ))}
      {/* Attributions */}
      <div className="space-y-3">
        <Skel className="h-5 w-48" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Skel key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      </div>
      {/* Chart */}
      <Skel className="h-52 rounded-xl w-full" />
    </div>
  );
}
